
// Deployment trigger: 2026-04-24
// Common Game Components
import React, { useState } from 'react';
import { Mail, CheckCircle2, Trophy, XCircle, Download, X } from 'lucide-react';
import { Prize } from '../../types';

export const GameWrapper: React.FC<{ children: React.ReactNode, isLightMode?: boolean }> = ({ children, isLightMode }) => {
  return (
    <div className="flex flex-col items-center w-full min-h-[500px] justify-center perspective-1000">
      <div className={`w-full max-w-[420px] rounded-[32px] p-6 sm:p-10 text-center shadow-2xl border relative overflow-hidden transition-all duration-500 ${isLightMode ? 'bg-white border-slate-200' : 'bg-[#0b0f1a] border-[#1e293b]'}`}>
        {children}
      </div>
    </div>
  );
};

interface LeadFormProps {
  name: string;
  email: string;
  phone: string;
  setName: (v: string) => void;
  setEmail: (v: string) => void;
  setPhone: (v: string) => void;
  onSubmit: () => void;
  isLightMode?: boolean;
}

export const LeadForm: React.FC<LeadFormProps> = ({ name, email, phone, setName, setEmail, setPhone, onSubmit, isLightMode }) => {
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [agreed, setAgreed] = useState(true);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const formatPhone = (value: string) => {
    const cleaned = ('' + value).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (match) {
      const parts = [];
      if (match[1]) parts.push(match[1]);
      if (match[2]) parts.push(match[2]);
      if (match[3]) parts.push(match[3]);
      return parts.join('-');
    }
    return value;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    setPhone(formatted);
    
    if (formatted && !/^\d{3}-\d{3}-\d{4}$/.test(formatted)) {
      setPhoneError('Invalid phone format (XXX-XXX-XXXX)');
    } else {
      setPhoneError('');
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setEmail(val);
    
    if (val && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) {
      setEmailError('Invalid email format');
    } else {
      setEmailError('');
    }
  };

  const canClaim = name.trim().length > 0 && 
                   email.trim().length > 0 && 
                   phone.trim().length > 0 &&
                   !emailError && 
                   !phoneError &&
                   agreed;

  return (
    <div className="animate-in slide-in-from-bottom-8 duration-500">
      <Mail className={`w-12 h-12 mx-auto mb-6 ${isLightMode ? 'text-indigo-600' : 'text-indigo-400'}`} />
      <h3 className={`text-2xl font-black mb-2 uppercase italic ${isLightMode ? 'text-slate-800' : 'text-white'}`}>Claim Reward</h3>
      <p className="text-slate-500 text-[10px] uppercase font-black tracking-widest mb-8">Enter your details to receive your prize</p>
      <div className="space-y-4 mb-6 text-left">
        <label className="block">
          <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1.5 block ml-1">Full Name</span>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Alex Smith" className={`w-full px-6 py-4 rounded-2xl border outline-none text-xs font-bold focus:border-indigo-500 transition-all ${isLightMode ? 'bg-slate-50 border-slate-200 text-slate-800' : 'bg-slate-900/50 border-white/5 text-white'}`} />
        </label>
        <label className="block">
          <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1.5 block ml-1">Email Address</span>
          <input type="email" value={email} onChange={handleEmailChange} placeholder="alex@example.com" className={`w-full px-6 py-4 rounded-2xl border outline-none text-xs font-bold transition-all ${emailError ? 'border-red-500 focus:border-red-500' : 'focus:border-indigo-500'} ${isLightMode ? 'bg-slate-50 border-slate-200 text-slate-800' : 'bg-slate-900/50 border-white/5 text-white'}`} />
          {emailError && <span className="text-red-500 text-[10px] mt-1 ml-1 block">{emailError}</span>}
        </label>
        <label className="block">
          <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest mb-1.5 block ml-1">Phone Number</span>
          <input type="tel" value={phone} onChange={handlePhoneChange} placeholder="555-000-0000" maxLength={12} className={`w-full px-6 py-4 rounded-2xl border outline-none text-xs font-bold transition-all ${phoneError ? 'border-red-500 focus:border-red-500' : 'focus:border-indigo-500'} ${isLightMode ? 'bg-slate-50 border-slate-200 text-slate-800' : 'bg-slate-900/50 border-white/5 text-white'}`} />
          {phoneError && <span className="text-red-500 text-[10px] mt-1 ml-1 block">{phoneError}</span>}
        </label>
      </div>

      <div className="mb-8 text-left flex items-start gap-3">
        <input 
          type="checkbox" 
          id="consentCheckbox"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          className="mt-1 w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
        />
        <label htmlFor="consentCheckbox" className={`text-[10px] leading-tight ${isLightMode ? 'text-slate-500' : 'text-slate-400'}`}>
          By checking this box, you agree to receive text messages and emails regarding your prize and future promotions. Reply STOP to opt out. Message & data rates may apply. Read our <button type="button" onClick={() => setShowPrivacy(true)} className="text-indigo-500 underline hover:text-indigo-600">Privacy Policy</button>.
        </label>
      </div>

      <button disabled={!canClaim} onClick={onSubmit} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-6 rounded-3xl font-black uppercase tracking-widest disabled:opacity-50 shadow-xl shadow-indigo-900/40 transition-all active:scale-95">Submit & Claim</button>

      {showPrivacy && (
        <div 
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowPrivacy(false);
          }}
        >
          <div className={`w-full max-w-md p-6 rounded-3xl shadow-2xl text-left relative ${isLightMode ? 'bg-white' : 'bg-slate-900 border border-slate-800'}`}>
            <button 
              onClick={() => setShowPrivacy(false)}
              className={`absolute top-4 right-4 p-2 rounded-full transition-colors ${isLightMode ? 'text-slate-400 hover:bg-slate-100' : 'text-slate-500 hover:bg-slate-800'}`}
            >
              <X className="w-5 h-5" />
            </button>
            <h4 className={`text-lg font-bold mb-4 ${isLightMode ? 'text-slate-900' : 'text-white'}`}>Privacy Policy</h4>
            <div className={`text-xs space-y-3 max-h-[60vh] overflow-y-auto pr-2 ${isLightMode ? 'text-slate-600' : 'text-slate-300'}`}>
              <p>We respect your privacy and are committed to protecting your personal data. This privacy policy explains how we collect, use, and share your information when you participate in our promotions.</p>
              <p><strong>Information Collection:</strong> We collect your name, email address, and phone number when you enter our games or promotions.</p>
              <p><strong>Use of Information:</strong> Your information is used to deliver your prize, communicate with you about your entry, and send you marketing communications (if you have opted in).</p>
              <p><strong>SMS Consent:</strong> By providing your phone number and checking the consent box, you agree to receive SMS messages from us. Message and data rates may apply. You can opt-out at any time by replying STOP.</p>
              <p><strong>Data Sharing:</strong> We do not sell your personal information to third parties. We may share your data with service providers who assist us in operating our promotions and communications.</p>
            </div>
            <button 
              onClick={() => setShowPrivacy(false)}
              className="mt-6 w-full py-3 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-xl font-bold text-sm transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export const SuccessView: React.FC<{ prize: Prize | null, onNext: () => void, isLightMode?: boolean }> = ({ prize, onNext, isLightMode }) => {
  const isLoss = prize?.type === 'empty';
  return (
    <div className="animate-in slide-in-from-bottom-8 duration-500">
      <div className={`w-24 h-24 ${isLoss ? 'bg-slate-500/10 text-slate-400' : 'bg-emerald-500/10 text-emerald-400'} rounded-[32px] flex items-center justify-center mx-auto mb-8 border overflow-hidden ${isLightMode ? 'border-slate-200' : 'border-white/10'}`}>
        {isLoss ? (
          <XCircle className="w-12 h-12" />
        ) : prize?.imageUrl ? (
          <img src={prize.imageUrl} alt={prize?.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
        ) : (
          <Trophy className="w-12 h-12" />
        )}
      </div>
      <h3 className={`text-4xl font-black mb-4 uppercase italic tracking-tighter leading-none ${isLightMode ? 'text-slate-800' : 'text-white'}`}>{isLoss ? 'HARD LUCK' : 'YOU WON!'}</h3>
      <div className={`p-8 rounded-[40px] mb-12 border-2 border-dashed flex items-center justify-center min-h-[120px] ${isLightMode ? 'bg-slate-50 border-slate-200' : 'bg-slate-900/50 border-white/10'}`}>
        <span className={`text-xl font-black uppercase italic leading-tight ${isLightMode ? 'text-indigo-600' : 'text-indigo-400'}`}>{prize?.name}</span>
      </div>
      {!isLoss && (
        <button onClick={onNext} className="w-full bg-indigo-600 text-white py-6 rounded-3xl font-black uppercase tracking-widest shadow-xl active:scale-95 transition-all">Get Your Reward</button>
      )}
      {isLoss && (
        <button onClick={() => window.location.reload()} className={`w-full py-6 rounded-3xl font-black uppercase tracking-widest ${isLightMode ? 'bg-slate-100 text-slate-600 hover:bg-slate-200' : 'bg-white/5 text-white'}`}>Try Again</button>
      )}
    </div>
  );
};

export const TicketView: React.FC<{ ticketNumber: string, endDate?: string, onDone: () => void, isLightMode?: boolean }> = ({ ticketNumber, endDate, onDone, isLightMode }) => (
  <div className="text-center animate-in zoom-in duration-500">
    <div className="w-24 h-24 bg-emerald-500/10 text-emerald-400 rounded-[32px] flex items-center justify-center mx-auto mb-8 border border-emerald-500/20"><CheckCircle2 className="w-12 h-12" /></div>
    <h3 className={`text-3xl font-black mb-2 uppercase italic tracking-tighter leading-none ${isLightMode ? 'text-slate-800' : 'text-white'}`}>You're In!</h3>
    <p className="text-slate-500 text-[10px] mb-10 font-bold tracking-widest uppercase">Drawing on: {endDate || 'Soon'}</p>
    <div className={`border-2 border-dashed rounded-[32px] p-8 mb-10 relative overflow-hidden ${isLightMode ? 'bg-indigo-50 border-indigo-200' : 'bg-[#1e234a] border-indigo-500/40'}`}>
       <span className={`font-bold text-[10px] uppercase tracking-[0.3em] block mb-2 ${isLightMode ? 'text-indigo-600' : 'text-indigo-400'}`}>Ticket Number</span>
       <span className={`font-black text-4xl tracking-widest block ${isLightMode ? 'text-slate-800' : 'text-white'}`}>{ticketNumber}</span>
    </div>
    <button onClick={onDone} className={`w-full py-6 rounded-3xl font-black uppercase tracking-widest flex items-center justify-center gap-3 transition-all ${isLightMode ? 'bg-slate-100 text-slate-600 hover:bg-slate-200' : 'bg-white/5 hover:bg-white/10 text-white'}`}><Download className="w-5 h-5" /> Done</button>
  </div>
);

export const CountdownUnit = ({ value, label, isLightMode }: { value: number, label: string, isLightMode?: boolean }) => (
  <div className="flex flex-col items-center">
    <div className={`w-12 h-14 rounded-2xl flex items-center justify-center border shadow-xl ${isLightMode ? 'bg-white border-slate-200' : 'bg-[#1e293b] border-white/5'}`}>
      <span className={`font-black text-xl ${isLightMode ? 'text-slate-800' : 'text-white'}`}>{value.toString().padStart(2, '0')}</span>
    </div>
    <span className="text-[8px] font-black text-slate-500 uppercase tracking-widest mt-2">{label}</span>
  </div>
);
